import { useEffect, useMemo, useState } from 'react';
import { useChannelFeed, useStore } from '../../store/StoreContext';
import { SignalActivityChart, type ChartSettings } from '../../components/charts/SignalActivityChart';
import { AdditionalChannelsField } from '../../components/channels/AdditionalChannelsField';
import { useChannelTemplates } from '../../lib/useChannelTemplates';
import { fetchSignals } from '../../api/dataApi';
import type { ChannelRef, Message } from '../../types';

// How many extra channels can be overlaid on one chart - purely a
// legibility cap (more than a handful of lines/colors on one chart stops
// being readable), unrelated to the Multi-Channel Report's
// backend-enforced MaxAdditionalChannels, though it happens to match.
const MAX_ADDITIONAL_CHANNELS = 4;

// FetchedSignal (fetchSignals' return shape) already carries everything
// SignalActivityChart's chart-comparison path reads except `isMine`,
// which it never uses - mirrors PublicChannelViewScreen's identical
// chartMessages mapping.
function toChartMessages(signals: { id: string; sender: string; text: string; date: string; type: string; value?: string }[]): Message[] {
  return signals?.map((m) => ({
    id: m.id,
    sender: m.sender,
    text: m.text,
    date: m.date,
    isMine: false,
    type: !m.type || m.type === 'message' ? undefined : m.type,
    value: m.value,
  }));
}

export function ChartScreen({ channelId }: { channelId: string }) {
  const { channel, templates, updateChannelChartType, getChartCompareChannels, setChartCompareChannels, setChartSettings } = useStore();
  const c = channel(channelId);
  const feedMessages = useChannelFeed(channelId).messages;
  // This channel's own fully-hydrated templates take precedence over the
  // capped global `templates` list, so chart series render in the colours
  // the owner actually picked instead of falling back to grey when the
  // channel's template is outside that 20-item window. Hook must run before
  // the early return below.
  const channelTemplates = useChannelTemplates(c);

  // Extra channels to overlay on this chart for comparison - see
  // CLAUDE_ZMIST_TECHNICAL_PLAN.md's Multi-Channel model, applied here to
  // charts rather than AI reports. Purely client-side: unlike Multi-Channel
  // Reports there's no backend job, just fetching each additional
  // channel's own first page of signals the same way this screen already
  // fetches its own (see StoreContext.loadMessages / GET /signals).
  //
  // Kept in the store (keyed by channelId), not screen-local useState: this
  // screen unmounts/remounts on every tab switch and every round trip to
  // /channels/pick, and a local-state selection used to silently reset to
  // empty on each of those - the store survives all of them for free.
  const additionalChannels = getChartCompareChannels(channelId);
  const setAdditionalChannels = (channels: ChannelRef[]) => setChartCompareChannels(channelId, channels);

  const [additionalMessages, setAdditionalMessages] = useState<Record<string, Message[]>>({});
  useEffect(() => {
    const missing = additionalChannels.filter((ch) => !(ch.id in additionalMessages));
    if (missing.length === 0) return;
    let cancelled = false;
    Promise.all(
      missing.map(async (ch) => {
        try {
          const page = await fetchSignals(ch.id);
          return [ch.id, toChartMessages(page.signals)] as const;
        } catch {
          return [ch.id, [] as Message[]] as const;
        }
      })
    ).then((entries) => {
      if (cancelled) return;
      setAdditionalMessages((prev) => {
        const next = { ...prev };
        for (const [id, msgs] of entries) next[id] = msgs;
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [additionalChannels, additionalMessages]);

  const compareChannels = useMemo(
    () =>
      additionalChannels.map((ch) => ({
        channel: ch,
        messages: additionalMessages[ch.id] ?? [],
        loading: !(ch.id in additionalMessages),
      })),
    [additionalChannels, additionalMessages]
  );

  if (!c) return null;

  return (
    <div className="flex flex-col gap-4 h-full overflow-y-auto pb-4">
      <AdditionalChannelsField
        channels={additionalChannels}
        onChange={setAdditionalChannels}
        excludeId={c.id}
        max={MAX_ADDITIONAL_CHANNELS}
        pickerTitle="Add channels to this chart"
        noun="chart"
        info={`Add other channels to compare with ${c.title}. The same channels are used by Reports and AI questions.`}
      />
      <SignalActivityChart
        messages={feedMessages}
        templates={[...channelTemplates, ...templates]}
        chartType={c.viewChartType}
        onChartTypeChange={(v) => updateChannelChartType(v, c.id)}
        primaryChannel={c}
        compareChannels={compareChannels}
        rollupChannelId={c.id}
        initialSettings={c.chartSettings as Partial<ChartSettings>}
        onSettingsChange={(settings) => setChartSettings(c.id, settings)}
      />
    </div>
  );
}
